import React, { useEffect, useState } from 'react';
import { Button, Container, Typography, Paper, List, ListItem, ListItemText, IconButton, Breadcrumbs, Link as MuiLink, CircularProgress } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { downloadDataArchive, downloadDataFile, fetchDataList } from '../api';

const getErrorMessage = async (err, fallbackMessage) => {
  const responseData = err?.response?.data;
  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      const parsed = JSON.parse(text);
      return parsed?.detail || text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  }

  return responseData?.detail || err?.message || fallbackMessage;
};

const normalizeFolder = (folder, currentPath) => {
  if (typeof folder === 'string') {
    return {
      name: folder,
      path: currentPath ? `${currentPath.replace(/\/$/, '')}/${folder}` : folder,
      zip_allowed: true,
      zip_disabled_reason: null
    };
  }

  const folderName = folder?.name || '';
  return {
    ...folder,
    name: folderName,
    path: folder?.path || (currentPath ? `${currentPath.replace(/\/$/, '')}/${folderName}` : folderName),
    zip_allowed: folder?.zip_allowed !== false,
    zip_disabled_reason: folder?.zip_disabled_reason || null
  };
};

const DataFilesPage = () => {
  const [path, setPath] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingArchive, setDownloadingArchive] = useState(null);

  useEffect(() => {
    loadList(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const loadList = async (p) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchDataList(p);
      setFolders((data.folders || []).map((folder) => normalizeFolder(folder, p)));
      setFiles(data.files || []);
    } catch (err) {
      console.error('Failed to load data list', err);
      setError('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const enterFolder = (folderName) => {
    const segment = folderName.split('/').filter(Boolean).join('/');
    const newPath = path ? `${path.replace(/\/$/, '')}/${segment}` : segment;
    setPath(newPath);
  };

  const getFolderPath = (folder) => folder.path || (path ? `${path.replace(/\/$/, '')}/${folder.name}` : folder.name);

  const downloadFile = async (filePath) => {
    try {
      const { blob, filename } = await downloadDataFile(filePath);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || filePath.split('/').pop() || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed', err);
      setError(await getErrorMessage(err, 'Download failed'));
    }
  };

  const downloadFolderArchive = async (folder, event) => {
    event.stopPropagation();
    const folderPath = getFolderPath(folder);

    if (folder.zip_allowed === false) {
      setError(folder.zip_disabled_reason || 'Folder archive download failed');
      return;
    }

    try {
      setDownloadingArchive(folderPath);
      setError(null);
      const { blob, filename } = await downloadDataArchive(folderPath);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${folder.name}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Folder archive download failed', err);
      setError(await getErrorMessage(err, 'Folder archive download failed'));
    } finally {
      setDownloadingArchive(null);
    }
  };

  const breadcrumbs = path ? path.split('/').filter(Boolean) : [];

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 2, mt: 2 }}>Data files</Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink component="button" onClick={() => setPath('')}>Data</MuiLink>
          {breadcrumbs.map((b, idx) => {
            const subpath = breadcrumbs.slice(0, idx + 1).join('/');
            return (
              <MuiLink key={subpath} component="button" onClick={() => setPath(subpath)}>{b}</MuiLink>
            );
          })}
        </Breadcrumbs>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <List>
          {loading && (
            <ListItem>
              <CircularProgress />
            </ListItem>
          )}

          {error && (
            <ListItem>
              <ListItemText primary={error} />
            </ListItem>
          )}

          {folders.map((folder) => {
            const folderPath = getFolderPath(folder);
            const isPreparingArchive = downloadingArchive === folderPath;
            const secondaryText = folder.zip_allowed === false ? folder.zip_disabled_reason : null;

            return (
            <ListItem
              button
              key={folderPath}
              onClick={() => enterFolder(folder.name)}
              secondaryAction={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(event) => downloadFolderArchive(folder, event)}
                  disabled={isPreparingArchive || folder.zip_allowed === false}
                >
                  {isPreparingArchive ? 'Preparing...' : 'ZIP'}
                </Button>
              }
              sx={{ cursor: 'pointer' }}
            >
              <FolderIcon sx={{ mr: 2 }} />
              <ListItemText primary={folder.name} secondary={secondaryText} />
            </ListItem>
            );
          })}

          {files.map((file) => (
            <ListItem key={file.path} secondaryAction={
              <IconButton edge="end" aria-label="download" onClick={() => downloadFile(file.path)} sx={{ cursor: 'pointer' }}>
                <DownloadIcon />
              </IconButton>
            }>
              <InsertDriveFileIcon sx={{ mr: 2 }} />
              <ListItemText primary={file.name} secondary={file.size ? `${file.size} bytes` : null} sx={{ cursor: 'default' }} />
            </ListItem>
          ))}

          {(!loading && folders.length === 0 && files.length === 0) && (
            <ListItem>
              <ListItemText primary="No files or folders found" />
            </ListItem>
          )}
        </List>
      </Paper>
    </Container>
  );
};

export default DataFilesPage;
